import { createContext } from 'preact'
import { useContext, useEffect, useState } from 'preact/hooks'
import fomService from '../services/fomService.js'
import dataProviderService from '../services/dataProviderService.js'
import { init as initLocalization } from '../services/localization.js'
import { useRef } from 'react'
import WebPlayActionCardService from '../services/WebPlayActionCardService'
import { registerActionCardComponent } from '../actionCardModule.js'
import { init as initAnalitics } from '../services/analytics.js'
import { useTranslation } from 'react-i18next'
import { applyCssToShadow } from '../utils.js'
import { trackRequested } from '../services/trackService.js'

async function addWebPlayCustomCss(webPlayInfoResp) {
  try {
    const cssLinks = webPlayInfoResp.customCssFiles?.split(',') ?? []

    const shadowDom = document.querySelector('action-cards').shadowRoot
    for (const cssLink of cssLinks) {
      await applyCssToShadow(shadowDom, cssLink)
    }
  } catch (ex) {
    console.error(ex)
  }
}

/**
 * If webPlay requires login than only parent page can get webPlay info (using authProxy)
 */
async function getWebPlayInfoFromParentPage() {
  return new Promise(resolve => {
    const info = window.webPlayInfo

    if (!info) {
      resolve(null)
      return
    }

    // Waiting info from parent page (see index.html)
    if (info.isLoading === true) {
      const interval = setInterval(() => {
        if (window.webPlayInfo?.isLoading === undefined) {
          clearInterval(interval)
          resolve(window.webPlayInfo)
        }
      }, 300) // polling interval
      return
    }

    // Take info from parent page (see index.html)
    resolve(info)
  })
}

export const Context = createContext()

const AppContext = ({ children, initialProps: { urlParams, name, playbookId, showScore, email } }) => {
  const [state, setState] = useState(null)
  const [error, setError] = useState(null)
  const acRootRef = useRef(null)
  const { t } = useTranslation()

  useEffect(() => {
    ;(async () => {
      const webPlayInfoResp = (await getWebPlayInfoFromParentPage()) ?? (await fomService.getWebPlayInfo(playbookId))
      // 401 - Unauthorized
      if (webPlayInfoResp.statusCode == 401) {
        setError(
          t('app.loginRequired', 'This content is protected and doesn’t support embedding. Please contact support.'),
        )
        return
      }
      if (webPlayInfoResp.weglotApiKey) {
        initLocalization(webPlayInfoResp.weglotApiKey)
      }

      initAnalitics(webPlayInfoResp)

      trackRequested(playbookId)

      const viewMode = webPlayInfoResp.enableFunnelView
        ? 'funnel'
        : webPlayInfoResp.enableDocumentView
        ? 'doc'
        : 'default'

      const actionCard = await fomService.getActionCard({
        email,
        headersCount: webPlayInfoResp.headersCount,
        actionCardUrl: webPlayInfoResp.actionCardUrl,
        playbookId,
      })

      const dataProvider = await dataProviderService.getByWebPlay({
        playbookId,
        headersCount: webPlayInfoResp.headersCount,
        showScore,
        actionCard,
        viewMode,
        accentColor: webPlayInfoResp.accentColor,
      })

      dataProvider.onInit = actionCardService => {
        addWebPlayCustomCss(webPlayInfoResp)

        setState(prev => {
          const nextState = { ...prev }
          nextState.actionCardService = new WebPlayActionCardService(actionCardService)
          return nextState
        })
      }

      await registerActionCardComponent(dataProvider)

      setState({ ...webPlayInfoResp, actionCard, viewMode })
    })()
  }, [])

  const context = {
    urlParams,
    name,
    playbookId,
    showScore,
    email,
    acRootRef,
    actionCardService: new WebPlayActionCardService(),
    ...(state || {}),
  }

  useEffect(() => {
    useAppContext.getState = () => context
  }, [])

  if (error) {
    return <div className="text-red-600 p-4 text-center">{error}</div>
  }

  return <Context.Provider value={context}>{state && children}</Context.Provider>
}

export const useAppContext = () => useContext(Context)

export default AppContext
